/**
 * data-mode.ts
 *
 * Single source of truth for runtime data mode.
 * Evaluated at build time via Vite's `define` block or environment variables.
 *
 * Modes:
 * - 'production': Clean empty stores; no Alice/Bob/Carol; launches Owner Readiness Wizard.
 * - 'demo': Automatically pre-populates deterministic scenario fixtures on cold start.
 * - 'development-fixtures': Boots empty by default; exposes manual fixture injection.
 * - 'test': Strict deterministic clock, memory-only/isolated storage, mock model & transport providers.
 *
 * INVARIANT: An empty production workspace is valid and must remain empty.
 * Never re-seed based on an emptiness check.
 */

export type DataMode =
  | 'production'
  | 'demo'
  | 'development-fixtures'
  | 'test';

export const DATA_MODE: DataMode =
  ((globalThis as any).__VITE_DATA_MODE__ ??
    (import.meta as any).env?.VITE_DATA_MODE ??
    'production') as DataMode;

export const isProduction = (): boolean => DATA_MODE === 'production';
export const isDemo = (): boolean => DATA_MODE === 'demo';
export const isDevFixtures = (): boolean => DATA_MODE === 'development-fixtures';
export const isTest = (): boolean => DATA_MODE === 'test';
